import { useEffect } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { EUserRole } from 'enums';
import { useInviteUsersMutation } from 'store/apis/org.api';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';

interface UseInviteUsersFormManagerProps {
  next?: (data?: any) => void;
}

export const useInviteUsersFormManager = ({ next = () => {} }: UseInviteUsersFormManagerProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [inviteUsers, { isLoading, isSuccess, isError, ...rest }] = useInviteUsersMutation();

  const initialValues = {
    emails: [] as string[],
    role: EUserRole.BASIC,
    message: '',
  };

  const validationSchema = Yup.object().shape({
    emails: Yup.array()
      .of(Yup.string().email('Invalid email'))
      .min(1, 'Must have at least 1 email')
      .max(10, 'You can only invite up ot 10 people at a time')
      .test('emails', `Please remove the duplicated emails`, (emails) => {
        if (!emails) {
          return true;
        }

        if (emails.length <= 1) {
          return true;
        }

        for (let i = 0; i < (emails || []).length; i++) {
          if (emails.filter((item) => item === emails[i]).length > 1) {
            return false;
          }
        }

        return true;
      }),
    role: Yup.string().required(),
    message: Yup.string().nullable().max(1000, 'Must be at most 1000 characters'),
  });

  const formManager = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      inviteUsers({ orgSlug, data: values });
    },
  });

  useEffect(() => {
    if (!isLoading && isSuccess && !isError) {
      const { data } = rest;

      next(data);
    }
  }, [isLoading, isSuccess, isError]);

  return [formManager, { isLoading, isSuccess, isError }] as [
    FormikProps<typeof initialValues>,
    { isLoading: boolean; isSuccess: boolean; isError: boolean },
  ];
};
